/**
 * Hybrid retrieval — lexical + semantic, fused by rank (#626).
 *
 * WHY BOTH. Measured on the 1,197-entry catalog, the two methods fail in opposite
 * directions and neither is sufficient alone:
 *
 *   exact skill name typed   lexical 11/12 at #1   semantic 5/12  (cai-sf #11, pr-review #10)
 *   paraphrase of intent     lexical MISSES        semantic #4 of 1,197
 *
 * `deployment summary` → `ship-digest` is the canonical case: lexical cannot reach it
 * at all (no shared token — "summary" ≠ "Summarises"), semantic finds it at #4.
 * Conversely a user typing `cai-sf` verbatim expects it first, and semantic buries it.
 *
 * WHY RANK FUSION RATHER THAN SCORE FUSION. The two scores are not comparable:
 * MiniSearch/BM25 is unbounded and query-dependent (observed 6–100+), while cosine over
 * normalised vectors is compressed into roughly 0.26–0.33. Adding or averaging them lets
 * whichever happens to have the wider range dominate for reasons unrelated to relevance,
 * and normalising both per query is exactly the fiddly calibration RRF exists to avoid.
 * Reciprocal Rank Fusion uses only POSITION, so it needs no tuning and cannot be skewed
 * by a scale change in either engine.
 *
 * PURITY. This module deliberately imports nothing that needs a browser or credentials —
 * no `./env`, no AWS SDK. It takes a query VECTOR, it does not produce one. Embedding the
 * query requires Bedrock, which a Lambda can reach and a browser cannot, so that step
 * belongs to the caller. Keeping the fusion pure is what lets the SPA, a Lambda, an MCP
 * server, and the measurement scripts share one implementation.
 */

import type { MarketplaceEntry } from "../types/index";
import type { ScoredEntry } from "./search";

/** One semantic hit: an entry's `installName` and its cosine similarity to the query. */
export interface SemanticHit {
  installName: string;
  score: number;
}

/**
 * A fused candidate, carrying enough provenance to explain its position.
 *
 * The per-engine ranks are kept deliberately: "why is this here?" is the first question
 * anyone asks of a hybrid retriever, and without them a bad result is unattributable to
 * either engine.
 */
export interface RetrievalCandidate {
  entry: MarketplaceEntry;
  /** Combined RRF score. Comparable only within one result set. */
  fusedScore: number;
  /** 1-based rank in the lexical list, or undefined if that engine did not return it. */
  lexicalRank?: number;
  /** 1-based rank in the semantic list, or undefined. */
  semanticRank?: number;
}

/**
 * RRF's smoothing constant. 60 is the value from the original paper and the de facto
 * default across search stacks.
 *
 * It controls how sharply rank position is discounted: the contribution of rank r is
 * 1/(K + r), so with K=60 the gap between rank 1 and rank 2 is small while the gap
 * between rank 1 and rank 50 still matters. A much smaller K would make the #1 slot
 * dominate so heavily that the second engine could never influence the outcome —
 * which would defeat the point of fusing.
 */
export const RRF_K = 60;

/**
 * Per-engine weights. Swept against the 50-case golden set (`sweep-rrf-weights.ts`).
 *
 * 4:1 DOMINATES both lexical-only and the previous 3:1 on every metric — it is not a
 * trade-off:
 *
 *   weighting      recall  P@1  MRR   exact-name  many-similar  task-intent
 *   lexical only     76%   57%  0.60     100%         73%           58%
 *   4:1              98%   57%  0.66     100%         73%           58%   ← this
 *   3:1 (previous)   98%   52%  0.65      75%         64%           58%
 *   1:1             100%   50%  0.64      25%         64%           67%
 *   1:2             100%   40%  0.59       0%         36%           75%
 *
 * At 3:1 a weak semantic rank counted as enough evidence to demote a match lexical had
 * right, costing 25 points of exact-name and 9 of many-similar. At 4:1 those recover fully
 * while semantic still contributes its whole recall gain (76% → 98%).
 *
 * WHY 4 AND NOT 6 OR 8. Every ratio from 4:1 to 8:1 scores identically, because above 4:1
 * semantic stops influencing the top of the list at all and only adds recall deeper down.
 * 4:1 is the least extreme point in that plateau, so it preserves the most semantic
 * influence for queries the golden set does not cover.
 *
 * THE REMAINING TENSION IS REAL BUT NOT RESOLVABLE BY A CONSTANT. Semantic-heavy weights win
 * `task-intent` (75% at 1:2) and lose `exact-name` (0%); lexical-heavy weights do the
 * reverse. 4:1 wins overall only because exact-name + many-similar (15 cases) outnumber
 * task-intent (12). The ideal weighting is QUERY-DEPENDENT — a short token-like query
 * (`pr-review`) wants lexical, a sentence (`i need to summarize what changed before we
 * release`) wants semantic — which a single global constant cannot express. Adapting the
 * weight to query shape is the next measurable step, not more tuning of this number.
 */
export const DEFAULT_WEIGHTS = { lexical: 4, semantic: 1 } as const;

/**
 * Weighting for a query that reads as a sentence rather than an identifier.
 *
 * Equal weight, because for a described task the two engines are genuinely equally credible:
 * lexical still matches the words the user chose, and semantic reaches the entries that phrase
 * the same job differently.
 */
export const DESCRIPTIVE_WEIGHTS = { lexical: 1, semantic: 1 } as const;

/**
 * Above this many words, a query is treated as a description rather than a name.
 *
 * The exact value does not matter, which is the point — see `weightsForQuery`.
 */
export const IDENTIFIER_MAX_TOKENS = 2;

/**
 * Choose the RRF weighting from the SHAPE of the query.
 *
 * WHY THIS EXISTS. Every fixed ratio trades one case kind against another, because one constant
 * has to serve two different questions. Measured across the full pipeline:
 *
 *   weighting   exact-name P@1   task-intent P@1
 *   4:1              100%              58%
 *   1:1               25%              67%
 *   1:2                0%              75%
 *
 * Lexical-heavy protects "I typed a name"; semantic-heavy wins "I described a job". No middle
 * ratio gets both — it is a cliff, not a slope.
 *
 * But the query itself says which question it is. `pr-review` is one identifier-shaped token;
 * `i need to summarize what changed before we release` is a sentence. Picking per query beats
 * every constant, and beats the shipped 4:1 on EVERY metric while losing none:
 *
 *                     recall  P@1   MRR   MRR(lex-found)  exact-name  task-intent
 *   4:1                 98%   55%  0.65        0.78          100%        58%
 *   shape-adaptive     100%   57%  0.69        0.84          100%        67%
 *
 * The `MRR (lex-found)` column is the interesting one: computed over only the cases lexical
 * already found, it isolates ordering from recall. Fusion at a FIXED weight barely moved it
 * (0.76 → 0.78); adapting the weight moves it to 0.84. So a fixed weight buys recall only, while
 * an adaptive one genuinely orders better.
 *
 * WHY THIS IS NOT JUST OVERFITTING. A conditional rule fitted to 42 cases overfits more easily
 * than one constant, so the threshold was probed: 2, 3 and 4 tokens score IDENTICALLY, and the
 * short-query ratio works at both 4:1 and 8:1. A plateau across neighbouring values in both
 * parameters is the signature of a real effect. (Only ≤1 token is worse, which makes sense — a
 * two-word name like `code review` is still a name.)
 *
 * A hyphen/dot test was also tried (`/[-._]/`) on the theory that identifiers contain them. It did
 * not help beyond the token count and made the rule harder to explain, so it is not here.
 */
export function weightsForQuery(query: string): { lexical: number; semantic: number } {
  const tokens = query.trim().split(/\s+/).filter(Boolean).length;
  return tokens <= IDENTIFIER_MAX_TOKENS ? DEFAULT_WEIGHTS : DESCRIPTIVE_WEIGHTS;
}

/**
 * Rank every embedded entry against a query vector, best first.
 *
 * Vectors are unit-normalised at generation time (`normalize: true`), so cosine
 * similarity reduces to a dot product — cheaper, and it removes a class of bugs where
 * an unnormalised magnitude leaks into the ranking.
 *
 * An entry with no vector is simply absent from the results. That is the same "absence
 * is not an error" discipline as adoption and votes: an entry with no `installName`, or
 * one added since the artifact was generated, is unretrievable semantically rather than
 * a failure condition.
 */
export function semanticSearch(
  vectors: Record<string, number[]>,
  queryVector: number[],
  limit = 50,
): SemanticHit[] {
  const hits: SemanticHit[] = [];
  for (const installName in vectors) {
    const v = vectors[installName];
    // A dimension mismatch means the artifact was generated with a different model or
    // dimension setting than the query. Skipping is right: scoring across mismatched
    // spaces produces confident nonsense, which is worse than returning less.
    if (v.length !== queryVector.length) continue;
    let dot = 0;
    for (let i = 0; i < v.length; i++) dot += v[i] * queryVector[i];
    hits.push({ installName, score: dot });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, limit);
}

/**
 * Fuse a lexical result list and a semantic result list by Reciprocal Rank Fusion.
 *
 *   fused(entry) = Σ over engines that returned it of 1/(RRF_K + rank)
 *
 * An entry returned by only one engine still scores — that is the whole point, since it
 * is how a paraphrase-only match (semantic) and an exact-name match (lexical) both
 * survive. An entry returned by both scores higher than either alone.
 *
 * Entries are identified by `installName`, the catalog's build-time-unique id (the same
 * key adoption, votes and similarity all use). A lexical hit with no `installName`
 * cannot be fused — it has no identity to join on — so it is passed through on its
 * lexical rank alone rather than dropped.
 */
export function fuseRRF(
  lexical: ScoredEntry[],
  semantic: SemanticHit[],
  entryByInstallName: Map<string, MarketplaceEntry>,
  limit = 50,
  weights: { lexical: number; semantic: number } = DEFAULT_WEIGHTS,
): RetrievalCandidate[] {
  const byKey = new Map<string, RetrievalCandidate>();
  // Entries with no installName are keyed by object identity instead, so they neither
  // collide with each other nor silently vanish.
  const anonymous: RetrievalCandidate[] = [];

  lexical.forEach((hit, i) => {
    const rank = i + 1;
    const contribution = weights.lexical / (RRF_K + rank);
    const key = hit.entry.installName;
    if (!key) {
      anonymous.push({ entry: hit.entry, fusedScore: contribution, lexicalRank: rank });
      return;
    }
    const existing = byKey.get(key);
    if (existing) {
      // Same installName twice in the lexical list would be a malformed catalog; keep
      // the better (earlier) rank rather than double-counting it.
      if (existing.lexicalRank === undefined || rank < existing.lexicalRank) {
        existing.lexicalRank = rank;
      }
      return;
    }
    byKey.set(key, { entry: hit.entry, fusedScore: contribution, lexicalRank: rank });
  });

  semantic.forEach((hit, i) => {
    const rank = i + 1;
    const contribution = weights.semantic / (RRF_K + rank);
    const existing = byKey.get(hit.installName);
    if (existing) {
      existing.fusedScore += contribution;
      existing.semanticRank = rank;
      return;
    }
    // Semantic-only hit. Requires the entry to exist in the catalog we hold — a vector
    // for a since-removed entry is inert, matching Contract 9's stale-artifact rule.
    const entry = entryByInstallName.get(hit.installName);
    if (!entry) return;
    byKey.set(hit.installName, { entry, fusedScore: contribution, semanticRank: rank });
  });

  return [...byKey.values(), ...anonymous]
    .sort((a, b) => b.fusedScore - a.fusedScore)
    .slice(0, limit);
}

/** Convenience index builder — every consumer needs this to join semantic hits back. */
export function indexByInstallName(catalog: MarketplaceEntry[]): Map<string, MarketplaceEntry> {
  const m = new Map<string, MarketplaceEntry>();
  for (const e of catalog) if (e.installName) m.set(e.installName, e);
  return m;
}
